if (!customElements.get('wt-timeline')) {
  // console.log("Timeline inside")
    customElements.define('wt-timeline', class WtTimeline extends HTMLElement {
      constructor() {
        super();
        this.yearButtons = null;
        this.contentElements = null;
        this.activeYear = null;
        this.wrapper = null;
        
        // Bind methods
        this.handleYearClick = this.handleYearClick.bind(this);
        this.updateContentHeight = this.updateContentHeight.bind(this);
        this.handleResize = debounce(this.updateContentHeight.bind(this), 250);
      }
  
      connectedCallback() {
        // console.log('Timeline component connected'); // Debug
        this.init();
        
        // Add resize listener
        window.addEventListener('resize', () => {
          this.updateContentHeight();
          this.updateTimelineLineWidth();
        });
        
        // Set initial scroll position for mobile
        if (window.innerWidth <= 900) {
          requestAnimationFrame(() => {
            const activeButton = this.querySelector('.timeline__year-btn.active');
            if (activeButton) {
              const yearsContainer = this.querySelector('.timeline__years');
              const buttonLeft = activeButton.offsetLeft;
              const buttonWidth = activeButton.offsetWidth;
              const containerWidth = yearsContainer.offsetWidth;
              
              const scrollLeft = buttonLeft - (containerWidth / 2) + (buttonWidth / 2);
              
              yearsContainer.scrollTo({
                left: Math.max(0, scrollLeft),
                behavior: 'auto' // Use 'auto' for initial position to avoid animation
              });
            }
          });
        }
        this.updateTimelineLineWidth();
      }
  
      disconnectedCallback() {
        window.removeEventListener('resize', this.handleResize);
      }
  
      init() {
        // Find the right container for years and content
        const right = this.querySelector('.timeline__right');
        if (!right) {
          // console.log('Could not find .timeline__right');
          return;
        }
  
        this.yearButtons = right.querySelectorAll('.timeline__year-btn');
        this.contentElements = right.querySelectorAll('.timeline__content');
        this.wrapper = right.querySelector('.timeline__content-wrapper');
        this.activeYear = right.querySelector('.timeline__year-btn.active')?.dataset.year;
  
        // console.log('Timeline initialized with:', {
        //   buttons: this.yearButtons.length,
        //   contents: this.contentElements.length,
        //   activeYear: this.activeYear
        // });
  
        // Add click listeners to year buttons
        this.yearButtons.forEach(button => {
          button.addEventListener('click', this.handleYearClick);
        });
  
        // Add click listeners to dots
        this.dots = this.querySelectorAll('.timeline__dot');
        this.dots.forEach(dot => {
          dot.addEventListener('click', (event) => {
            const year = dot.dataset.year;
            const btn = Array.from(this.yearButtons).find(b => b.dataset.year === year);
            if (btn) btn.click();
          });
        });
  
        // Set initial content states
        this.contentElements.forEach(content => {
          if (content.dataset.year === this.activeYear) {
            content.classList.add('active');
            content.style.display = '';
          } else {
            content.classList.remove('active');
            content.style.display = 'none';
          }
        });
  
        // Initial height setup
        requestAnimationFrame(() => {
          this.updateContentHeight();
        });
        this.updateTimelineLineWidth();
      }
  
      handleYearClick(event) {
        const clickedYear = event.target.dataset.year;
        if (clickedYear === this.activeYear) return;
  
        // Handle mobile scrolling
        if (window.innerWidth <= 900) {
          const clickedButton = event.target;
          const yearsContainer = this.querySelector('.timeline__years');
          const allYears = Array.from(this.yearButtons);
          const currentIndex = allYears.findIndex(btn => btn.dataset.year === clickedYear);
          const previousIndex = allYears.findIndex(btn => btn.dataset.year === this.activeYear);
          
          // Get the container's bounding rect
          const containerRect = yearsContainer.getBoundingClientRect();
          const buttonRect = clickedButton.getBoundingClientRect();
          
          // Calculate positions relative to the container
          const buttonLeft = buttonRect.left - containerRect.left + yearsContainer.scrollLeft;
          const buttonWidth = buttonRect.width;
          const containerWidth = containerRect.width;
          
          // Calculate scroll amount based on direction
          let scrollAmount;
          if (currentIndex > previousIndex) {
            // Scrolling right - show more of the next year
            scrollAmount = buttonLeft - (containerWidth * 0.2);
          } else {
            // Scrolling left - show more of the previous year
            scrollAmount = buttonLeft - (containerWidth * 0.8);
          }
          
          // Ensure we don't scroll past the start
          scrollAmount = Math.max(0, scrollAmount);
          
          // Smooth scroll to the new position
          yearsContainer.scrollTo({
            left: scrollAmount,
            behavior: 'smooth'
          });
        }
  
        // Update active states
        this.yearButtons.forEach(button => {
          button.classList.toggle('active', button.dataset.year === clickedYear);
        });
  
        const prevContent = Array.from(this.contentElements).find(
          el => el.dataset.year === this.activeYear
        );
        const newContent = Array.from(this.contentElements).find(
          el => el.dataset.year === clickedYear
        );
  
        if (prevContent && newContent) {
          // Temporarily show the new content to measure it (invisible but in normal flow)
          newContent.style.display = 'flex';
          newContent.style.visibility = 'hidden';
          newContent.style.opacity = '0';
          
          // Force reflow and measure
          void newContent.offsetWidth;
          const newHeight = newContent.offsetHeight;
          
          // Hide it again immediately
          newContent.style.display = 'none';
          newContent.style.visibility = '';
          
          // Update wrapper height before animation
          if (this.wrapper) {
            this.wrapper.style.height = `${newHeight}px`;
          }
  
          // Animate out previous content
          prevContent.style.opacity = '0';
          prevContent.style.transform = 'translateY(-1rem)';
  
          // After animation, show and animate in new content
          setTimeout(() => {
            prevContent.classList.remove('active');
            prevContent.style.display = 'none';
            prevContent.style.opacity = '';
            prevContent.style.transform = '';
            
            // Show new content and animate it in
            newContent.style.display = 'flex';
            newContent.style.opacity = '0';
            newContent.classList.add('active');
            
            // Force reflow then animate
            void newContent.offsetWidth;
            newContent.style.opacity = '1';
          }, 300);
        }
  
        // Update dots
        const dots = this.querySelectorAll('.timeline__dot');
        dots.forEach(dot => {
          dot.classList.toggle('active', dot.dataset.year === clickedYear);
        });
  
        // Update images
        const imageWrappers = this.querySelectorAll('.timeline__img-wrapper');
        imageWrappers.forEach(wrapper => {
          if (wrapper.dataset.year === clickedYear) {
            wrapper.style.display = 'block';
            // Force reflow
            void wrapper.offsetWidth;
            wrapper.classList.add('active');
          } else {
            wrapper.classList.remove('active');
            // Wait for fade out before hiding
            setTimeout(() => {
              if (!wrapper.classList.contains('active')) {
                wrapper.style.display = 'none';
              }
            }, 300);
          }
        });
  
        this.activeYear = clickedYear;
      }
  
      updateContentHeight() {
        if (!this.wrapper) return;
        const activeContent = Array.from(this.contentElements).find(
          el => el.dataset.year === this.activeYear
        );
        if (activeContent) {
          // console.log('Updating height to:', activeContent.offsetHeight); // Debug
          this.wrapper.style.height = `${activeContent.offsetHeight}px`;
        }
      }
  
      updateTimelineLineWidth() {
        if (window.innerWidth > 900) return; // Only for mobile
        const yearsContainer = this.querySelector('.timeline__years');
        const line = yearsContainer.querySelector('.timeline__line');
        const dotsLabels = yearsContainer.querySelector('.timeline__dots-labels');
        if (line && dotsLabels) {
          // Set the line width to match the scrollable width of the dots container
          line.style.width = dotsLabels.scrollWidth + 'px';
        }
      }
    });
  }
  
  // Utility function for debouncing resize events
  function debounce(fn, delay) {
    let timeoutId;
    return function (...args) {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => fn.apply(this, args), delay);
    };
  }